
import { useState } from "react";
import { Subscription } from "@/types/subscription";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface SubscriptionFormPaymentProps {
  subscription: Subscription | null;
  onPaymentError: (error: string | null) => void;
  discountAmount?: number;
}

export function useSubscriptionFormPayment({
  subscription,
  onPaymentError,
  discountAmount = 0
}: SubscriptionFormPaymentProps) {
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  const handlePayment = async () => {
    if (!subscription) return;
    
    setIsProcessingPayment(true);
    onPaymentError(null);
    
    try {
      console.log("Initiating payment for subscription:", subscription);
      
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { subscription, discountAmount }
      });
      
      if (error) {
        console.error("Supabase function error:", error);
        throw new Error(`Function error: ${error.message}`);
      }
      
      if (!data) {
        throw new Error("No data returned from payment server");
      }
      
      console.log("Checkout session response:", data);
      
      // Redirect to Stripe Checkout - Fix for payment page not loading
      if (data.url) {
        // Using window.location.href to navigate directly to the Stripe checkout URL
        // This ensures a full page navigation which is more reliable than opening in a new tab
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned from payment server");
      }
    } catch (error) {
      console.error("Payment error:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to initialize payment";
      onPaymentError(errorMessage);
      toast({
        title: "Payment Error",
        description: errorMessage,
        variant: "destructive"
      });
      setIsProcessingPayment(false);
    }
  };

  return {
    isProcessingPayment,
    handlePayment
  };
}
